import React, { type ElementRef, type FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import type { SingleValueProps, OptionProps } from '@atlaskit/select';
import Select, { components } from '@atlaskit/select';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { expValEquals } from '@confluence/feature-experiments';

import type { Role } from '../../../../commonTypes.tsx';

export type PrincipalAccessProps = {
	isLockedOut: boolean;
	access: Role;
	isEditable: boolean;
	isCanViewDisabled: boolean;
	isUnpublishedDraft: boolean;
	hasInheritedRestrictions: boolean;
	edition: ConfluenceEdition;
	onChange: (_: Role) => void;
	onRemove: () => void;
	/**
	 * used for a11y purposes
	 */
	focusRef: React.MutableRefObject<ElementRef<typeof Select>> | null;
	isSaving: boolean;
};

interface Option {
	label: string | JSX.Element;
	value: Role | 'remove';
	toolTipDescription?: string;
	isDisabled?: boolean;
}

const i18n = defineMessages({
	inheritedRestrictionText: {
		id: 'share-and-restrict-dialog.principal-access.inherited-restrictions',
		defaultMessage: 'Everyone on the parent can already view',
		description: 'tooltip text for a page with inherited restrictions',
	},
	noInheritedRestrictionText: {
		id: 'share-and-restrict-dialog.principal-access.no-inherited-restrictions',
		defaultMessage: 'Everyone in this space can already view',
		description: 'tooltip text for a page with no inherited restrictions',
	},
	canViewAccessText: {
		id: 'share-and-restrict-dialog.principal-access.can-view-access',
		defaultMessage: 'Can view',
		description: 'Indicates that a user has permission to view the page',
	},
	canEditAccessText: {
		id: 'share-and-restrict-dialog.principal-access.can-edit-access',
		defaultMessage: 'Can edit',
		description: 'Indicates that a user has permission to edit the page',
	},
	removeAccessText: {
		id: 'share-and-restrict-dialog.principal-access.remove-access',
		defaultMessage: 'Remove',
		description: 'Principal access for "Remove" access text',
	},
	noAccessText: {
		id: 'share-and-restrict-dialog.principal-access.no-access',
		defaultMessage: 'No access',
		description: 'Principal access for "No access" access text',
	},
});

const noTextWrap = xcss({
	whiteSpace: 'nowrap',
});

const tooltipHeight = xcss({
	height: '24px',
});

const tooltipText = xcss({
	font: 'font.body.small',
});

const editPrincipalAccessStyle = xcss({
	width: 'auto',
});

const uneditablePrincipalAccessStyle = xcss({
	paddingInline: 'space.100',
	whiteSpace: 'nowrap',
});

const containerStyle = xcss({
	flexShrink: 0,
});

const makeOptions = (
	isCanViewDisabled: boolean,
	hasInheritedRestrictions: boolean,
	isUnpublishedDraft: boolean,
	access: Role,
	formatMessage: Function,
): Option[] => {
	if (isUnpublishedDraft && access !== 'can-view') {
		return [
			{
				label: formatMessage(i18n.canEditAccessText),
				value: 'can-edit',
			},
			{
				label: formatMessage(i18n.removeAccessText),
				value: 'remove',
			},
		];
	}
	return [
		{
			label: formatMessage(i18n.canViewAccessText),
			value: 'can-view',
			isDisabled: isCanViewDisabled,
			toolTipDescription: hasInheritedRestrictions
				? formatMessage(i18n.inheritedRestrictionText)
				: formatMessage(i18n.noInheritedRestrictionText),
		},
		{
			label: formatMessage(i18n.canEditAccessText),
			value: 'can-edit',
		},
		{
			label: formatMessage(i18n.removeAccessText),
			value: 'remove',
		},
	];
};

const getAccessText = (access: Role) => {
	if (access === 'can-view') return i18n.canViewAccessText;
	else if (access === 'can-edit') return i18n.canEditAccessText;
	else return i18n.noAccessText;
};

const UneditablePrincipalAccess: FC<{
	access: Role;
}> = ({ access }) => {
	return (
		<Box xcss={uneditablePrincipalAccessStyle}>
			<FormattedMessage {...getAccessText(access)} />
		</Box>
	);
};

const EditablePrincipalAccess: FC<Omit<PrincipalAccessProps, 'isEditable' | 'edition'>> = ({
	isLockedOut,
	access,
	isCanViewDisabled,
	hasInheritedRestrictions,
	isUnpublishedDraft,
	onChange,
	onRemove,
	focusRef,
	isSaving,
}) => {
	const { formatMessage } = useIntl();

	const options = makeOptions(
		isCanViewDisabled,
		hasInheritedRestrictions,
		isUnpublishedDraft,
		access,
		formatMessage,
	);

	const CustomSingleValue = (props: SingleValueProps<Option>) => {
		return (
			<components.SingleValue {...props}>
				<Text>{props.data.label}</Text>
			</components.SingleValue>
		);
	};

	const CustomOption = (props: OptionProps<Option>) => {
		const isDisabledViewAccess = props.data.label === 'Can view' && props.data.isDisabled;

		const renderCustomOption = () => {
			if (isDisabledViewAccess) {
				return (
					<Inline alignBlock="center" shouldWrap={false}>
						<Box xcss={noTextWrap}>{props.data.label}</Box>
						<Box xcss={tooltipHeight}>
							<Tooltip
								content={
									<Box xcss={[tooltipText, noTextWrap]}>{props.data.toolTipDescription}</Box>
								}
								position="right"
							>
								<StatusInformationIcon
									label="edit-icon"
									spacing="spacious"
									color={token('color.icon')}
								/>
							</Tooltip>
						</Box>
					</Inline>
				);
			}
			if (props.data.value === 'remove') {
				return <Text color="color.text.accent.red">{props.data.label}</Text>;
			}
			return <Text>{props.data.label}</Text>;
		};
		return <components.Option {...props}>{renderCustomOption()}</components.Option>;
	};

	return (
		<Box xcss={editPrincipalAccessStyle}>
			<Select
				appearance="subtle"
				spacing="compact"
				isDisabled={
					isLockedOut ||
					(isSaving && expValEquals('persistence_specific_access_gate', 'cohort', 'experiment'))
				}
				options={options}
				isSearchable={false}
				value={options.find((option) => option.value === access)}
				menuPosition="fixed"
				onChange={(selectedOption) => {
					if (!selectedOption) return;
					if (selectedOption.value === 'remove') onRemove();
					else onChange(selectedOption.value);
				}}
				components={{ SingleValue: CustomSingleValue, Option: CustomOption }}
				ref={focusRef}
			/>
		</Box>
	);
};

export const PrincipalAccess: FC<PrincipalAccessProps> = ({
	isEditable,
	edition,
	...otherProps
}) => {
	const isFreeEdition = edition === ConfluenceEdition.FREE;

	return (
		<Box xcss={containerStyle}>
			{isEditable && !isFreeEdition ? (
				<EditablePrincipalAccess {...otherProps} />
			) : (
				<UneditablePrincipalAccess access={otherProps.access} />
			)}
		</Box>
	);
};
